<template>
    <div class="ai-page-fullscreen">
        <!-- 直接使用全屏组件，它自带左侧边栏和完整布局 -->
        <FullScreenChat
            :key="agentInstanceKey"
            :auth-token="authToken"
            :user-id="userId"
            :app-id="appId"
            :token-check-url="aiAgentTokenCheckUrl"
            :login-redirect="aiLoginRedirect"
            @exit="handleExitMode"
        />
    </div>
</template>

<script>
export default {
    name: 'AiIndex'
}
</script>

<script setup>
import { ref, watch, computed, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import useUserStore from '@/store/modules/user'
import { getToken } from '@/utils/auth'
import { FullScreenChat } from 'chat-box'
import { Navbar } from '@/layout/components'

const router = useRouter()
const userStore = useUserStore()
const route = useRoute()

// 获取 token 和 userId
const authToken = computed(() => userStore.token || getToken() || '')
const userId = computed(() => userStore.userId || '')
const appId = ref('') // 示例AppId，请替换为真实值

/** 业务系统 API 前缀，需与主站接口代理一致（如 utils/request/index.js 的 baseURL） */
const BUSINESS_API_PREFIX = '/cloud'
const AI_AGENT_TOKEN_CHECK_PATH = '/dashboard/v3/ops/agent/token/check'
const aiAgentTokenCheckUrl = `${String(BUSINESS_API_PREFIX).replace(/\/$/, '')}${AI_AGENT_TOKEN_CHECK_PATH}`

/** hash 路由下登录页应走 /#/login，避免出现 /login?...#/index 的错位 URL */
const aiLoginRedirect = computed(() => `/#/login?redirect=${encodeURIComponent(route.fullPath)}`)

// footerHeight 固定为 195px
const footerHeight = ref(195)
// 用于强制重新渲染的计数器
const chatBoxKey = ref(0)
const agentInstanceKey = computed(() => `ai-fullscreen-${chatBoxKey.value}-${userId.value || 'anonymous'}`)
const chatBoxRef = ref(null)

// 网页列表相关
const webPages = ref([])
const webPageCount = ref(0)
const showWebPagesSidebar = ref(false)

// 监听路由变化，确保 footerHeight 正确设置
watch(() => route.path, (newPath) => {
    if (newPath.includes('/ai')) {
        footerHeight.value = 195
        chatBoxKey.value++ // 强制重新渲染
    }
}, { immediate: true })

/**
 * 处理退出模式
 */
function handleExitMode() {
    if (window.history.length > 1) {
        router.back()
    } else {
        router.push('/')
    }
}

/**
 * 处理 thought 事件，更新网页列表
 */
function handleThought(data) {
    if (data && data.webPages) {
        webPages.value = data.webPages
        webPageCount.value = data.webPageCount || data.webPages.length
    } else {
        webPages.value = []
        webPageCount.value = 0
        showWebPagesSidebar.value = false
    }
}

/**
 * 处理切换网页列表显示/隐藏
 */
function handleToggleWebPages(data) {
    if (data && data.webPages) {
        // 如果当前显示的网页列表和点击的是同一个，则收起
        if (showWebPagesSidebar.value && webPages.value.length > 0) {
            showWebPagesSidebar.value = false
            webPages.value = []
            webPageCount.value = 0
        } else {
            // 否则显示新的网页列表
            webPages.value = data.webPages
            webPageCount.value = data.webPageCount || data.webPages.length
            showWebPagesSidebar.value = true
        }

        // 更新 has-sidebar 类
        nextTick(() => {
            const rightContent = document.querySelector('.ai-right-content')
            if (rightContent) {
                if (showWebPagesSidebar.value) {
                    rightContent.classList.add('has-sidebar')
                } else {
                    rightContent.classList.remove('has-sidebar')
                }
            }
        })
    }
}

/**
 * 打开网页
 */
function openWebPage(url) {
    if (url) {
        window.open(url, '_blank')
    }
}
</script>

<style scoped lang="scss">
.ai-page-fullscreen {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    background: linear-gradient(108.83deg, #E5F6FF 0.85%, #F8F8F8 49.22%, #F1FFFC 99.49%);
    z-index: 1000;
}

/* 左侧边栏 */
.ai-left-sidebar {
    width: 280px;
    height: 100%;
    display: flex;
    flex-direction: column;
    background: linear-gradient(171.81deg, rgba(231, 237, 255, 0.7) -5.12%, rgba(255, 255, 255, 0.7) 27.04%);
    padding: 24px;
    justify-content: space-between;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.ai-left-header {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 16px;
}

.ai-icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.ai-icon {
    width: 64px;
    height: 64px;
    object-fit: contain;
    animation: rotate 20s linear infinite;
}

@keyframes rotate {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

.ai-title {
    font-family: 'HarmonyOS Sans SC', sans-serif;
    font-weight: 500;
    font-size: 32px;
    line-height: normal;
    color: #2e2e2e;
    text-align: left;
    white-space: nowrap;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.ai-left-footer {
    display: flex;
    justify-content: center;
    padding-bottom: 24px;
}

.exit-mode-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 294px;
    height: 50px;
    padding: 0;
    background: #e9f3fd;
    border: none;
    border-radius: 15px;
    color: #374151;
    font-family: 'HarmonyOS Sans SC', sans-serif;
    font-size: 14px;
    font-weight: 400;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
        background: #d4e7fc;
    }

    .exit-mode-icon {
        flex-shrink: 0;
        width: 24px;
        height: 24px;
    }
}

/* 右侧内容区 */
.ai-right-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    padding: 0 320px 0 320px;
    min-width: 0;
}

/* Navbar 样式 */
.ai-right-content :deep(.navbar) {
    flex-shrink: 0;
}

:deep(.navbar-right) {
    position: fixed;
    right: 0;
}

/* 聊天容器 */
.ai-chat-container {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;
    transition: transform 0.3s ease;

    // 确保 ChatBox 充分利用宽度
    :deep(.chat-box-container) {
        width: 100%;
        height: 100%;
        max-width: 100%;
    }

    :deep(.chat-box-body) {
        width: 100%;
        max-width: 100%;
    }

    :deep(.el-bubble-list) {
        width: 100%;
        max-width: 100%;
    }

    :deep(.el-bubble-item) {
        width: 100%;
        max-width: 100%;
    }

    :deep(.el-bubble-content) {
        display: flex;
        justify-content: flex-end;
        width: 100%;
        max-width: 100%;
    }

    :deep(.el-bubble-content-loading) {
        display: flex;
        justify-content: flex-start;
    }

    :deep(.el-bubble-content-wrapper) {
        width: 100%;
        max-width: 100%;
    }

    :deep(.ai-message) {
        width: 100%;
        max-width: 100%;
    }

    :deep(.content-section) {
        width: 100%;
        max-width: 100%;
    }

    :deep(.push-content-section) {
        width: 100%;
        max-width: 100%;
    }

    :deep(.chat-box-babble) {
        white-space: normal !important;
        word-wrap: break-word !important;
        word-break: break-word !important;
        overflow-wrap: break-word !important;
        background: #242424 !important;
        opacity: 0.9 !important;
        color: #fff !important;
        display: inline-block !important;
        max-width: 100% !important;
    }

    :deep(.el-timeline) {
        max-width: none !important;
        width: 100% !important;
    }

    // 思考链字体大小、颜色和间距调整
    :deep(.thought-title-text) {
        font-size: 14px;
        color: #2E2E2E;
        padding: 12px 8px 8px 8px;
    }

    :deep(.el-timeline-item__timestamp) {
        font-size: 13px;
        color: #2E2E2E;
        margin: 2px 0 4px 0 !important;
    }

    :deep(.el-collapse-item__title) {
        font-size: 13px;
        color: #2E2E2E;
    }

    :deep(.typer-container) {
        font-size: 13px;
        color: #2E2E2E;
    }

    :deep(.el-timeline-item) {
        padding-bottom: 8px !important;
    }
}

/* 当有网页列表时，向左推一点（使用 transform 保持宽度不变） */
.ai-right-content.has-sidebar .ai-chat-container {
    transform: translateX(-180px);
}

.ai-right-content.has-sidebar .ai-disclaimer {
    transform: translateX(-180px);
}

/* 免责声明 */
.ai-disclaimer {
    flex-shrink: 0;
    padding: 8px 0;
    text-align: center;
    font-size: 12px;
    color: rgba(46, 46, 46, 0.6);
    font-family: 'HarmonyOS Sans SC', sans-serif;
    font-weight: 300;
    transition: transform 0.3s ease;
}

/* 右侧边栏：网页列表 */
.ai-right-sidebar {
    position: fixed;
    right: 0;
    top: 60px;
    bottom: 0;
    width: 360px;
    background: transparent;
    backdrop-filter: blur(10px);
    display: flex;
    flex-direction: column;
    z-index: 998;
    overflow: hidden;
    transform: translateX(100%);
    animation: slideInFromRight 0.3s ease forwards;
}

@keyframes slideInFromRight {
    from {
        transform: translateX(100%);
    }

    to {
        transform: translateX(0);
    }
}

.web-pages-list {
    flex: 1;
    overflow-y: auto;
    padding: 16px;
}

.web-page-item {
    padding: 16px;
    margin-bottom: 12px;
    background: rgba(255, 255, 255, 0.6);
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.05);
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: rgba(255, 255, 255, 0.9);
        border-color: rgba(0, 0, 0, 0.1);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    }

    &:last-child {
        margin-bottom: 0;
    }
}

.web-page-source {
    font-family: 'HarmonyOS Sans SC', sans-serif;
    font-size: 12px;
    color: rgba(46, 46, 46, 0.6);
    margin-bottom: 8px;
}

.web-page-title {
    font-family: 'HarmonyOS Sans SC', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #2e2e2e;
    line-height: 1.5;
    margin-bottom: 8px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;

    :deep(em) {
        color: #1890ff;
        font-style: normal;
    }
}

.web-page-desc {
    font-family: 'HarmonyOS Sans SC', sans-serif;
    font-size: 12px;
    color: rgba(46, 46, 46, 0.7);
    line-height: 1.5;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;

    :deep(em) {
        color: #1890ff;
        font-style: normal;
    }
}
</style>
